/**
 * TokenProvenanceOverlay.jsx — GRANULLO DEBUG TOOL
 * 
 * Dev toggle that shows token lineage on any element.
 * Helps prove canonical compliance and debug drift.
 * 
 * Usage:
 * - Press Ctrl+Shift+T to toggle overlay
 * - Hover over any element to see computed styles + token sources
 * - Only visible in development mode
 */

import React, { useState, useEffect, useCallback } from 'react';

// Canonical token map for reverse lookup
const CANONICAL_TOKENS = {
  // Colors
  '#5b5cff': '--g-primary',
  '#7b5cff': '--g-primary-2',
  '#e24b6a': '--g-accent-pink / --g-drift / --g-danger',
  '#19c6b7': '--g-accent-teal',
  '#f2b233': '--g-accent-amber / --g-warning',
  '#2cc56f': '--g-accent-mint / --g-success',
  '#3a8bff': '--g-info / --g-evidence',
  
  // Surfaces (light mode)
  '#f6f7fb': '--g-bg',
  '#f1f3fa': '--g-bg-2',
  '#ffffff': '--g-surface-solid',
  
  // Text
  'rgba(16, 20, 36, 0.92)': '--g-text',
  'rgba(16, 20, 36, 0.78)': '--g-text-secondary',
  'rgba(16, 20, 36, 0.6)': '--g-muted',
  'rgba(16, 20, 36, 0.46)': '--g-muted-2',
};

export default function TokenProvenanceOverlay() {
  const [isActive, setIsActive] = useState(false);
  const [hoveredElement, setHoveredElement] = useState(null);
  const [elementInfo, setElementInfo] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  
  // Only enable in development
  const isDev = process.env.NODE_ENV === 'development';
  
  // Toggle handler
  const handleKeyDown = useCallback((e) => {
    if (e.ctrlKey && e.shiftKey && e.key === 'T') {
      e.preventDefault();
      setIsActive(prev => !prev);
    }
  }, []);
  
  // Mouse move handler
  const handleMouseMove = useCallback((e) => {
    if (!isActive) return;
    
    const element = document.elementFromPoint(e.clientX, e.clientY);
    if (!element || element === hoveredElement) return;
    
    setHoveredElement(element);
    setPosition({ x: e.clientX + 15, y: e.clientY + 15 });
    
    // Get computed styles
    const computed = window.getComputedStyle(element);
    const info = {
      tagName: element.tagName.toLowerCase(),
      className: element.className,
      id: element.id,
      styles: {
        color: computed.color,
        backgroundColor: computed.backgroundColor,
        borderColor: computed.borderColor,
        fontFamily: computed.fontFamily,
        fontSize: computed.fontSize,
        fontWeight: computed.fontWeight,
      },
      tokens: [],
    };
    
    // Check for token usage
    Object.entries(info.styles).forEach(([prop, value]) => {
      const tokenName = CANONICAL_TOKENS[value];
      if (tokenName) {
        info.tokens.push({ property: prop, value, token: tokenName });
      }
    });
    
    // Check CSS variables in inline styles
    const inlineStyle = element.getAttribute('style') || '';
    const varMatches = inlineStyle.match(/var\(--[\w-]+\)/g) || [];
    varMatches.forEach(v => {
      info.tokens.push({ property: 'inline', value: v, token: v });
    });
    
    setElementInfo(info);
  }, [isActive, hoveredElement]);
  
  useEffect(() => {
    if (!isDev) return;
    
    document.addEventListener('keydown', handleKeyDown);
    
    if (isActive) {
      document.addEventListener('mousemove', handleMouseMove);
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isDev, isActive, handleKeyDown, handleMouseMove]);
  
  if (!isDev || !isActive) return null;
  
  return (
    <>
      {/* Overlay indicator */}
      <div
        style={{
          position: 'fixed',
          top: 8,
          right: 8,
          background: '#5b5cff',
          color: 'white',
          padding: '4px 8px',
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          zIndex: 99999,
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        TOKEN DEBUG ON (Ctrl+Shift+T to toggle)
      </div>
      
      {/* Element info tooltip */}
      {elementInfo && (
        <div
          style={{
            position: 'fixed',
            left: position.x,
            top: position.y,
            background: 'rgba(16, 20, 36, 0.95)',
            color: '#f0f0f4',
            padding: 12,
            borderRadius: 6,
            fontSize: 11,
            fontFamily: 'Monaco, Consolas, monospace',
            maxWidth: 400,
            zIndex: 99999,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            pointerEvents: 'none',
          }}
        >
          <div style={{ marginBottom: 8, borderBottom: '1px solid #3a3e48', paddingBottom: 8 }}>
            <span style={{ color: '#5b5cff' }}>&lt;{elementInfo.tagName}&gt;</span>
            {elementInfo.id && <span style={{ color: '#f2b233' }}> #{elementInfo.id}</span>}
            {elementInfo.className && (
              <div style={{ color: '#808088', fontSize: 10, marginTop: 2 }}>
                .{typeof elementInfo.className === 'string' ? elementInfo.className.split(' ').slice(0, 3).join(' .') : ''}
              </div>
            )}
          </div>
          
          <div style={{ marginBottom: 8 }}>
            <div style={{ color: '#808088', marginBottom: 4 }}>Computed Styles:</div>
            {Object.entries(elementInfo.styles).map(([prop, value]) => (
              <div key={prop} style={{ display: 'flex', gap: 8 }}>
                <span style={{ color: '#19c6b7' }}>{prop}:</span>
                <span style={{ color: '#f0f0f4' }}>{value?.slice?.(0, 30) || value}</span>
              </div>
            ))}
          </div>
          
          {elementInfo.tokens.length > 0 && (
            <div style={{ borderTop: '1px solid #3a3e48', paddingTop: 8 }}>
              <div style={{ color: '#2cc56f', marginBottom: 4 }}>✓ Token Provenance:</div>
              {elementInfo.tokens.map((t, i) => (
                <div key={i} style={{ color: '#f2b233' }}>
                  {t.property}: {t.token}
                </div>
              ))}
            </div>
          )}
          
          {elementInfo.tokens.length === 0 && (
            <div style={{ borderTop: '1px solid #3a3e48', paddingTop: 8, color: '#e24b6a' }}>
              ⚠ No canonical tokens detected
            </div>
          )}
        </div>
      )}
    </>
  );
}
